/**
 * In-drawer add to cart for 'bb-cart-recommendations'.
 *
 * Adds via the Ajax API and then does NOTHING to the DOM. <cart-items-component>
 * owns its own re-render: it knows its section id, listens for the standard cart
 * event and redraws itself with the theme's view transitions.
 *
 * An earlier version fetched the section and morphed `.cart-drawer__inner`
 * itself. That container holds cart-items-component, so two renders raced - ours
 * landed first with freshly built progress meters that had no previous width to
 * animate from, and the component's own render then found nothing left to change.
 * The bars jumped. Adding from a product card animated fine the whole time,
 * which is what gave it away.
 *
 * Rule for anything added here later: dispatch the event, let the theme render.
 *
 * No navigation fallback. If the add fails the button is restored and a short
 * message appears in place - the customer stays exactly where they were.
 */

const root = () => window.Shopify?.routes?.root || '/';

/**
 * @param {object} detail - Parsed cart/add.js response, passed on so any
 *   listener that wants the rendered sections can read them.
 */
async function announceCartUpdate(detail) {
  const events = await import('@shopify/events');
  const name = events?.StandardEvents?.cartLinesUpdate;
  if (!name) throw new Error('cartLinesUpdate event name unavailable');

  const event = new Event(name, { bubbles: true });
  event.action = 'add';
  event.source = 'bb-cart-recommendations';
  event.promise = Promise.resolve({ detail: { ...detail, didError: false } });
  document.dispatchEvent(event);
}

/** @param {HTMLFormElement} form */
function showInlineError(form) {
  if (form.parentElement?.querySelector('.bb-cart-recs__error')) return;

  const note = document.createElement('span');
  note.className = 'bb-cart-recs__error';
  note.setAttribute('role', 'status');
  note.textContent = 'Gick inte att lägga till';
  form.parentElement?.appendChild(note);

  setTimeout(() => note.remove(), 4000);
}

document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  if (!form.classList.contains('bb-cart-recs__form')) return;

  event.preventDefault();

  const button = form.querySelector('button');
  if (button) {
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
  }

  try {
    const body = new FormData(form);
    // Ask Shopify to render the drawer section alongside the add, so the
    // payload carries everything a listener could need without a second trip.
    body.append('sections', 'cart-drawer-section');
    body.append('sections_url', window.location.pathname);

    const response = await fetch(`${root()}cart/add.js`, {
      method: 'POST',
      body,
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) throw new Error(`cart/add returned ${response.status}`);

    await announceCartUpdate(await response.json());
  } catch (error) {
    console.warn('[bb-cart-recs] add failed, staying put:', error);
    if (button) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
    showInlineError(form);
  }
});
